
import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Send, Clock, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePodpislonSignature } from "@/hooks/usePodpislonSignature";

interface DocumentSignatureSectionProps {
  signatureId: string;
  clientId: string;
  generateContract: boolean;
  onChange: (field: 'generateContract' | 'signatureId', value: boolean | string) => void;
}

export function DocumentSignatureSection({
  signatureId,
  clientId,
  generateContract,
  onChange
}: DocumentSignatureSectionProps) {
  const { toast } = useToast();
  const [clientPhone, setClientPhone] = useState('');
  
  const {
    isLoading: isSendingSignature,
    signatureStatus,
    sendForSignature,
    checkSignatureStatus,
    cancelSignature
  } = usePodpislonSignature();

  // Mock signature options (можно заменить на реальные данные)
  const signatureOptions = [
    { id: '1', name: 'Подпись от 15.06.2024' },
    { id: '2', name: 'Подпись от 10.06.2024' },
    { id: '3', name: 'Подпись от 05.06.2024' }
  ];

  const handleSendForSignature = async () => {
    if (!clientPhone) {
      toast({
        title: "Ошибка",
        description: "Введите номер телефона клиента",
        variant: "destructive",
      });
      return;
    }

    if (!clientId) {
      toast({
        title: "Ошибка", 
        description: "Клиент не выбран",
        variant: "destructive",
      });
      return;
    }

    try {
      // Создаем фиктивный документ для отправки (в реальной системе будет генерироваться из данных заказа)
      const mockDocument = new Blob(['Contract content for order'], { type: 'application/pdf' });
      
      const newSignatureId = await sendForSignature({
        orderId: 'temp-order-id', // В реальном случае будет ID заказа
        phoneNumber: clientPhone,
        document: mockDocument,
        clientName: 'Клиент', // Получить из данных клиента
        documentType: 'contract'
      });

      if (newSignatureId) {
        onChange('signatureId', newSignatureId);
      }
    } catch (error: unknown) {
      console.error('Error sending signature:', error);
    }
  };

  const handleCheckStatus = async () => {
    if (!signatureId) {
      toast({
        title: "Ошибка",
        description: "Нет активного запроса на подпись",
        variant: "destructive",
      });
      return;
    }

    await checkSignatureStatus(signatureId);
  };

  const handleCancelSignature = async () => {
    if (!signatureId) return;
    
    try {
      const success = await cancelSignature(signatureId);
      if (success) {
        onChange('signatureId', '');
      }
    } catch (e: unknown) {
      console.error('Signature load failed', e);
    }
  };

  const getStatusIcon = () => {
    switch (signatureStatus) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'signed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (signatureStatus) {
      case 'pending':
        return 'Ожидает подпись';
      case 'signed':
        return 'Подписан';
      case 'rejected':
        return 'Отклонен';
      case 'expired':
        return 'Истек срок';
      default:
        return '';
    }
  };

  const handleGenerateContractToggle = (checked: boolean | string) => {
    const next = typeof checked === 'string' ? checked === 'true' : !!checked;
    onChange('generateContract', next);
  };

  return (
    <div className="bg-muted/50 rounded-lg border p-4">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <FileText className="mr-2 h-5 w-5 text-blue-600" />
        Документооборот и подпись
      </h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="generate-contract" className="text-sm font-medium">
            Генерировать договор
          </Label>
          <Switch
            id="generate-contract"
            checked={generateContract}
            onCheckedChange={handleGenerateContractToggle}
          />
        </div>

        {/* СМС подпись через podpislon.ru */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              СМС подпись через podpislon.ru
            </Label>
            {signatureStatus !== 'idle' && (
              <div className="flex items-center space-x-2">
                {getStatusIcon()}
                <span className="text-xs text-muted-foreground">
                  {getStatusText()}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-2">
            <Input
              placeholder="Номер телефона клиента"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              disabled={isSendingSignature}
            />
            <Button
              onClick={handleSendForSignature}
              disabled={isSendingSignature || !clientId}
              size="sm"
            >
              {isSendingSignature ? (
                <Clock className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Отправить
            </Button>
          </div>

          {/* Управление активной подписью */}
          {signatureId && signatureStatus !== 'idle' && (
            <div className="flex space-x-2 pt-2">
              <Button
                onClick={handleCheckStatus}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Проверить статус
              </Button>
              
              {signatureStatus === 'pending' && (
                <Button
                  onClick={handleCancelSignature}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Отменить
                </Button>
              )}
            </div>
          )}
          
          <p className="text-xs text-muted-foreground">
            Документ будет отправлен клиенту по СМС через сервис podpislon.ru для электронной подписи
          </p>
        </div>

        {/* Существующие подписи */}
        {clientId && (
          <div>
            <Label htmlFor="signature">Существующие подписи клиента</Label>
            <Select value={signatureId} onValueChange={(value) => onChange('signatureId', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите подпись" />
              </SelectTrigger>
              <SelectContent>
                {signatureOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
